// @ts-nocheck
import React from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Loader2, MapPin, Navigation } from 'lucide-react';
import { PublicEventHeader } from '@/components/public/PublicEventHeader';
import { usePublicLive, useDivisionParam } from '@/hooks/usePublicLive';

const FN = 'rle-public-schedule';
const NAVY = '#0a1f44';

/** Open the device's own maps app: coordinates when we have them, address otherwise. */
function directionsHref(v: any) {
  const q =
    v.latitude != null && v.longitude != null
      ? `${v.latitude},${v.longitude}`
      : [v.name, v.full_address].filter(Boolean).join(' ');
  return `https://maps.google.com/?q=${encodeURIComponent(q)}`;
}

const PublicVenuesPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [division] = useDivisionParam();

  const { data, loading, errorCode } = usePublicLive(
    FN,
    { action: 'venues', slug },
    { enabled: !!slug },
  );

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (errorCode === 'NOT_PUBLISHED') {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="text-2xl font-bold text-foreground">
          This schedule hasn't been published yet.
        </h1>
      </div>
    );
  }

  if (errorCode === 'NOT_FOUND') {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="text-2xl font-bold text-foreground">Event not found.</h1>
      </div>
    );
  }

  if (errorCode || !data) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="text-2xl font-bold text-foreground">Could not load venues.</h1>
      </div>
    );
  }

  const event = data.event || {};
  const venues = data.venues || [];

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <PublicEventHeader
        slug={slug ?? ''} current="venues" division={division}
        event={event} branding={data.branding} tabs={data.tabs || []} teams={[]}
      />

      {venues.length === 0 ? (
        <p className="py-12 text-center text-muted-foreground">
          {event.venue_name
            ? `Games are played at ${event.venue_name}. No address has been posted yet.`
            : 'No venue details have been posted yet.'}
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {venues.map((v: any) => (
            <div key={v.id} className="rounded-lg border bg-card p-5 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-bold" style={{ color: NAVY }}>
                    {v.name}
                  </h2>
                  {v.full_address && (
                    <p className="mt-1 flex items-start gap-1.5 text-sm text-muted-foreground">
                      <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
                      {v.full_address}
                    </p>
                  )}
                  {v.courts?.length > 0 && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      {v.courts.length} court{v.courts.length === 1 ? '' : 's'}:{' '}
                      {v.courts.join(', ')}
                    </p>
                  )}
                </div>
                <Button asChild variant="outline" size="sm">
                  <a href={directionsHref(v)} target="_blank" rel="noopener noreferrer">
                    <Navigation className="h-4 w-4" />
                    Directions
                  </a>
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PublicVenuesPage;
